// src/components/OTPInput/Otp.jsx
import React, { useContext, useState, useEffect, useRef } from 'react';
import { RecoveryContext } from '../forgotpassword/Forgotpassword'; // keep your original path

const containerStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#f0f4f8',
};

const mainContainerStyle = {
  background: 'white',
  padding: '20px',
  boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.1)',
  borderRadius: '8px',
  width: '100%',
  maxWidth: 420,
};

const titleStyle = { fontWeight: 600, fontSize: 24, marginBottom: 16 };

const codeContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  gap: 8,
  marginBottom: 16,
};

const inputStyle = {
  width: 64,
  height: 48,
  textAlign: 'center',
  padding: 12,
  borderRadius: 8,
  border: '1px solid #d1d5db',
  fontSize: 18,
  backgroundColor: 'white',
  outline: 'none',
};

const buttonStyle = {
  width: '100%',
  height: 48,
  marginTop: 8,
  borderRadius: 8,
  border: 'none',
  fontSize: 16,
  color: 'white',
  backgroundColor: 'black',
  cursor: 'pointer',
};

const ResendOTP = () => {
  const {
    email,
    recoveryInfo,
    requestOTP,     // <- secure resend (server generates & sends)
    verifyOTP,      // <- secure verify (server validates)
    isFetching,
    error,
    setError,
  } = useContext(RecoveryContext);

  const [timerCount, setTimer] = useState(60);
  const [OTPinput, setOTPinput] = useState(['', '', '', '']); // 4-digit UI (kept)
  const [disableResend, setDisableResend] = useState(true);
  const [sending, setSending] = useState(false);
  const inputsRef = useRef([]);

  // Resolve email (prefer server-sourced one)
  const resolvedEmail = (recoveryInfo?.email || email || '').toString().trim();

  // Countdown for resend
  useEffect(() => {
    setDisableResend(true);
    setTimer(60);
    const interval = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(interval);
          setDisableResend(false);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []); // start on mount

  // Autofocus first input on mount
  useEffect(() => {
    inputsRef.current?.[0]?.focus();
  }, []);

  const restartTimer = () => {
    setDisableResend(true);
    setTimer(60);
    const interval = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(interval);
          setDisableResend(false);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  const handleResendOTP = async () => {
    if (disableResend || sending) return;
    if (!resolvedEmail) return alert('Missing email.');
    try {
      setSending(true);
      setError?.(null);
      await requestOTP(resolvedEmail); // ask backend to generate + send
      alert('A new code has been sent to your email.');
      restartTimer();
      setOTPinput(['', '', '', '']);
      inputsRef.current?.[0]?.focus();
    } catch (e) {
      console.error(e);
      alert('Failed to resend code. Try again in a moment.');
    } finally {
      setSending(false);
    }
  };

  const handleChange = (val, idx) => {
    // accept only digits
    const digit = val.replace(/\D/g, '').slice(0, 1);
    const next = [...OTPinput];
    next[idx] = digit;
    setOTPinput(next);

    // move focus
    if (digit && idx < 3) inputsRef.current[idx + 1]?.focus();
    if (!digit && idx > 0 && !next[idx]) inputsRef.current[idx - 1]?.focus();
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !OTPinput[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (!text) return;
    const arr = text.split('');
    const next = ['', '', '', ''];
    for (let i = 0; i < arr.length; i++) next[i] = arr[i];
    setOTPinput(next);
    inputsRef.current[Math.min(arr.length, 3)]?.focus();
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setError?.(null);
    const codeStr = OTPinput.join('');
    if (!codeStr || codeStr.length < 4) {
      return alert('Please enter the 4-digit code.');
    }
    try {
      await verifyOTP(codeStr); // context switches page to "reset" on success
    } catch (err) {
      // If your verifyOTP throws, show a generic message; provider also sets `error`
      alert('The code you entered is not correct or has expired. Try again or resend.');
    }
  };

  return (
    <div style={containerStyle}>
      <div style={mainContainerStyle}>
        <div style={titleStyle}>Email Verification</div>
        <div style={{ marginBottom: 16, fontSize: 14 }}>
          We sent a 4-digit code to <b>{resolvedEmail || 'your email'}</b>
        </div>

        <form onSubmit={onVerify}>
          <div style={codeContainerStyle} onPaste={handlePaste}>
            {Array.from({ length: 4 }).map((_, i) => (
              <input
                key={i}
                ref={(el) => (inputsRef.current[i] = el)}
                inputMode="numeric"
                maxLength={1}
                style={inputStyle}
                value={OTPinput[i]}
                onChange={(e) => handleChange(e.target.value, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
              />
            ))}
          </div>

          <button
            type="submit"
            style={buttonStyle}
            disabled={isFetching}
          >
            {isFetching ? 'Verifying…' : 'Verify Account'}
          </button>

          <div style={{ marginTop: 16, fontSize: 14, color: '#718096' }}>
            Didn’t receive code?{' '}
            <span
              onClick={handleResendOTP}
              style={{
                color: disableResend || sending ? '#a0aec0' : '#2563eb',
                cursor: disableResend || sending ? 'not-allowed' : 'pointer',
                textDecorationLine: disableResend || sending ? 'none' : 'underline',
              }}
            >
              {disableResend ? `Resend in ${timerCount}s` : sending ? 'Sending…' : 'Resend Code'}
            </span>
          </div>

          {error && (
            <div style={{ marginTop: 12, color: '#c00', fontSize: 13 }}>
              {String(error)}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ResendOTP;
